import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { KeyRound, Copy, Ban, ShieldAlert } from "lucide-react";


const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };

export default function ApiTokensAdmin() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [consumer, setConsumer] = useState("");
  const [canWrite, setCanWrite] = useState(false);
  const [months, setMonths] = useState(18);
  const [created, setCreated] = useState(null); // token appena generato (mostrato una volta)

  const load = () => api.get("/admin/api-tokens").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim() || !consumer.trim()) { toast.error("Nome e consumatore obbligatori"); return; }
    try {
      const scopes = canWrite ? ["read", "write"] : ["read"];
      const { data } = await api.post("/admin/api-tokens", {
        name: name.trim(), consumer: consumer.trim(), scopes, expires_days: Math.round(months * 30),
      });
      setCreated(data);
      setName(""); setConsumer(""); setCanWrite(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revocare definitivamente questo token? Le integrazioni che lo usano smetteranno di funzionare.")) return;
    try { await api.post(`/admin/api-tokens/${id}/revoke`); toast.success("Token revocato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Token copiato"); };

  return (
    <div className="space-y-6" data-testid="api-tokens-admin">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold text-forest">
          <KeyRound className="h-6 w-6" /> API &amp; Integrazione
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Token Bearer per consentire a sistemi esterni (es. AssieticaSuite) di leggere via API il catalogo prodotti e le
          tabelle tariffarie. Sola lettura, revocabili, con scadenza. Il token viene mostrato <b>una sola volta</b>.
        </p>
      </div>

      <Card className="rounded-xl border-border/60 p-5">
        <h2 className="mb-3 font-heading text-base font-semibold text-forest">Genera nuovo token</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Nome / descrizione</Label>
            <Input data-testid="token-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Integrazione AssieticaSuite" /></div>
          <div className="space-y-1.5"><Label>Consumatore</Label>
            <Input data-testid="token-consumer" value={consumer} onChange={(e) => setConsumer(e.target.value)} placeholder="Es. AssieticaSuite" /></div>
          <div className="space-y-1.5"><Label>Scadenza (mesi)</Label>
            <Input data-testid="token-months" type="number" min={1} max={36} value={months} onChange={(e) => setMonths(Number(e.target.value) || 1)} /></div>
          <div className="flex items-end">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-forest">
              <Checkbox data-testid="token-write" checked={canWrite} onCheckedChange={(v) => setCanWrite(!!v)} />
              Abilita anche scrittura (scope write) — <span className="text-muted-foreground">futuro</span>
            </label>
          </div>
        </div>
        <Button data-testid="token-create" onClick={create} className="mt-4 gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
          <KeyRound className="h-4 w-4" /> Genera token
        </Button>

        {created && (
          <div data-testid="token-created" className="mt-4 rounded-xl border border-terracotta/40 bg-terracotta/5 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-forest">
              <ShieldAlert className="h-4 w-4 text-terracotta" /> Copia subito questo token: non verrà più mostrato.
            </div>
            <div className="mt-2 flex items-center gap-2">
              <code data-testid="token-value" className="flex-1 break-all rounded-lg bg-white px-3 py-2 text-xs text-forest">{created.token}</code>
              <Button size="sm" variant="outline" onClick={() => copy(created.token)} className="gap-1"><Copy className="h-3.5 w-3.5" /> Copia</Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Usalo nell'header HTTP: <code>Authorization: Bearer {created.token.slice(0, 10)}…</code> · Scope: {created.scopes.join(", ")} · Scade il {fmt(created.expires_at)}
            </p>
          </div>
        )}
      </Card>

      <Card className="rounded-xl border-border/60">
        <div className="border-b border-border/60 px-5 py-3"><h2 className="font-heading text-base font-semibold text-forest">Token attivi</h2></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Consumatore</TableHead><TableHead>Nome</TableHead><TableHead>Scope</TableHead>
            <TableHead>Creato</TableHead><TableHead>Scade</TableHead><TableHead>Ultimo uso</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Nessun token generato.</TableCell></TableRow>}
            {items.map((t) => (
              <TableRow key={t.id} data-testid={`token-row-${t.id}`}>
                <TableCell className="font-medium text-forest">{t.consumer}</TableCell>
                <TableCell className="text-xs">{t.name}</TableCell>
                <TableCell className="text-xs">{(t.scopes || []).join(", ")}</TableCell>
                <TableCell className="text-xs">{fmt(t.created_at)}</TableCell>
                <TableCell className="text-xs">{fmt(t.expires_at)}</TableCell>
                <TableCell className="text-xs">{t.last_used_at ? fmt(t.last_used_at) : "—"}</TableCell>
                <TableCell>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${t.revoked ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                    {t.revoked ? "Revocato" : "Attivo"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {!t.revoked && (
                    <Button size="sm" variant="ghost" data-testid={`token-revoke-${t.id}`} onClick={() => revoke(t.id)} className="gap-1 text-red-600 hover:text-red-700">
                      <Ban className="h-3.5 w-3.5" /> Revoca
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
